import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Rating
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../../config';

const ReviewManagement = ({ authToken }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminReply, setAdminReply] = useState('');

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/reviews`);
      const data = await response.json();
      
      if (data.success) {
        setReviews(data.reviews);
      } else {
        setError(data.error || 'Failed to fetch reviews');
      }
    } catch (err) {
      setError('Failed to fetch reviews: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const handleMenuOpen = (event, review) => {
    setAnchorEl(event.currentTarget);
    setSelectedReview(review);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReview(null);
  };

  const handleApproveReview = async (reviewId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/reviews/${reviewId}/approve`, {
        method: 'PUT'
      });
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Review approved successfully');
        fetchReviews();
      } else {
        setError(data.error || 'Failed to approve review');
      }
    } catch (err) {
      setError('Failed to approve review: ' + err.message);
    }
    handleMenuClose();
  };

  const handleRejectReview = async (reviewId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/reviews/${reviewId}/reject`, {
        method: 'PUT'
      });
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Review rejected successfully');
        fetchReviews();
      } else {
        setError(data.error || 'Failed to reject review');
      }
    } catch (err) {
      setError('Failed to reject review: ' + err.message);
    }
    handleMenuClose();
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/reviews/${reviewId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Review deleted successfully');
        fetchReviews();
      } else {
        setError(data.error || 'Failed to delete review');
      }
    } catch (err) {
      setError('Failed to delete review: ' + err.message);
    }
    handleMenuClose();
  };

  const handleReplyToReview = (review) => {
    setSelectedReview(review);
    setAdminReply(review.admin_reply || '');
    setDialogOpen(true);
    setAnchorEl(null); // Close menu without clearing selectedReview
  };

  const handleSubmitReply = async () => {
    if (!selectedReview || !selectedReview.review_id) {
      setError('No review selected for reply');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/admin/reviews/${selectedReview.review_id}/reply`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          admin_reply: adminReply
        })
      });
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Reply submitted successfully');
        setDialogOpen(false);
        fetchReviews();
      } else {
        setError(data.error || 'Failed to submit reply');
      }
    } catch (err) {
      setError('Failed to submit reply: ' + err.message);
    }
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Review Management
        </Typography>
        <Button
          variant="contained"
          onClick={fetchReviews}
          sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={clearMessages} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={clearMessages} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell>Review</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reviews.map((review) => (
              <TableRow key={review.review_id}>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {review.product_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {review.product_id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {review.customer_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {review.customer_email}
                  </Typography>
                  {review.is_verified_purchase && (
                    <Chip label="Verified Purchase" size="small" color="success" />
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Rating value={review.rating} readOnly size="small" />
                    <Typography variant="body2">
                      {review.rating}/5
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 300 }}>
                    {review.review_text}
                  </Typography>
                  {review.admin_reply && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="caption" color="primary" fontWeight="bold">
                        Admin Reply:
                      </Typography>
                      <Typography variant="body2">
                        {review.admin_reply}
                      </Typography>
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={review.status}
                    color={getStatusColor(review.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(review.created_at).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={(e) => handleMenuOpen(e, review)}
                    size="small"
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedReview?.status === 'pending' && (
          <MenuItem onClick={() => handleApproveReview(selectedReview.review_id)}>
            <ListItemIcon>
              <CheckIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Approve</ListItemText>
          </MenuItem>
        )}
        {selectedReview?.status === 'pending' && (
          <MenuItem onClick={() => handleRejectReview(selectedReview.review_id)}>
            <ListItemIcon>
              <CloseIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Reject</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleReplyToReview(selectedReview)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Reply</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDeleteReview(selectedReview?.review_id)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Reply Dialog */}
      <Dialog open={dialogOpen} onClose={() => {
        setDialogOpen(false);
        setSelectedReview(null);
        setAdminReply('');
      }} maxWidth="md" fullWidth>
        <DialogTitle>
          Reply to Review by {selectedReview?.customer_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Original Review:
            </Typography>
            <Typography variant="body1" sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              {selectedReview?.review_text}
            </Typography>
          </Box>
          <TextField
            label="Admin Reply"
            value={adminReply}
            onChange={(e) => setAdminReply(e.target.value)}
            multiline
            rows={4}
            fullWidth
            placeholder="Enter your reply to the customer..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDialogOpen(false);
            setSelectedReview(null);
            setAdminReply('');
          }}>
            Cancel
          </Button>
          <Button onClick={handleSubmitReply} variant="contained">
            Submit Reply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewManagement;
